import HttpClient, { RequestEntryParams } from '@/bundles/Http/transport/HttpClient';
import { HttpMethods } from '@/bundles/Http/enum/HttpMethods';
import { Nullable } from '@/utils/types';

export default class HttpTransport {
  readonly client: HttpClient;

  constructor (client: HttpClient) {
    this.client = client;
  }

  /**
   * Make get request
   * @param {string} url
   * @param {any} query
   * @param controller
   * @param {object} options - Additional request options
   * @param {boolean} [options.persistOnRouteChange] - If true, request won't be canceled on route change
   * @return {Promise}
   * */
  public get (url: string, query: any = {}, controller: Nullable<AbortController> = null, { options }: { options?: { persistOnRouteChange?: boolean } } = {}) {
    return this.makeRequest({ method: HttpMethods.get, query, url, controller, options });
  }

  public post (url: string, body: any, controller: Nullable<AbortController> = null, onUploadProgress?: (event: ProgressEvent) => void) {
    return this.makeRequest({
      method: HttpMethods.post,
      data: body,
      url,
      controller,
      onUploadProgress,
    });
  }

  /**
   * Make put request
   * @param {string} url
   * @param {any} body
   * @return {Promise}
   * */
  public put (url: string, body: any) {
    return this.makeRequest({
      method: HttpMethods.put,
      data: body,
      url,
    });
  }

  /**
   * Make patch request
   * @param {string} url
   * @param {any} body
   * @return {Promise}
   * */
  public patch (url: string, body?: any) {
    return this.makeRequest({
      method: HttpMethods.patch,
      data: body,
      url,
    });
  }

  /**
   * Make delete request
   * @param {string} url
   * @return {Promise}
   * */
  public delete (url: string) {
    return this.makeRequest({
      method: HttpMethods.delete,
      url,
    });
  }

  /**
   * Make API request
   * @param {HttpMethods} method
   * @param {String} url
   * @param {any} [payload={}]
   * @param {any} [query={}]
   * @return {Promise<any>}
   * */
  private makeRequest ({
    method,
    url,
    query = {},
    data = {},
    controller,
    options,
    onUploadProgress,
  }: RequestEntryParams): Promise<any> {
    return this.client.request({ url, method, data, query, controller, options, onUploadProgress });
  }
}
